// src/modules/vehiclegroupsales/VehiclegroupsalesDashboard.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import DaterangePicker from "../../common/Daterangepicker";

// ==========================================
// 1. SHARED CONSTANTS & UTILITIES
// ==========================================
const formatCurrency = (amount) => {
  if (
    amount === undefined ||
    amount === null ||
    Number.isNaN(amount) ||
    !Number.isFinite(amount)
  )
    return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCurrencyWithDecimal = (amount) => {
  if (
    amount === undefined ||
    amount === null ||
    Number.isNaN(amount) ||
    !Number.isFinite(amount)
  )
    return "₹0.00";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// City name normalization map
const CITY_NORMALIZE_MAP = {
  Bangalore: "Bengaluru",
  Bengaluru: "Bengaluru",
  Mysore: "Mysuru",
  Mysure: "Mysuru",
  Vellore: "Vellore",
  Chennai: "Chennai",
};

// ==========================================
// 2. INFO TOOLTIP COMPONENT
// ==========================================
const InfoTooltip = ({ content, position = "top" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isVisible = isOpen || isHovered;

  const positionClasses = {
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-r border-b border-slate-700",
    bottom:
      "absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-l border-t border-slate-700",
    left: "absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-r border-t border-slate-700",
    right:
      "absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-l border-b border-slate-700",
  };

  return (
    <div className="relative inline-flex items-center ml-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          inline-flex items-center justify-center 
          w-4 h-4 rounded-full 
          transition-all duration-200
          ${
            isVisible
              ? "bg-amber-500 text-white ring-2 ring-amber-400 ring-offset-1 scale-110"
              : "bg-gray-200 text-gray-600 hover:bg-amber-100 hover:text-amber-600 hover:ring-2 hover:ring-amber-200"
          }
          focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2
        `}
        aria-label="Information"
        aria-expanded={isVisible}
        type="button"
      >
        <i className="ti ti-info-circle text-[10px]" />
      </button>

      {isVisible && (
        <div
          className={`absolute z-[9999] ${positionClasses[position]} w-64 bg-slate-800 text-white rounded-lg shadow-2xl p-3 text-xs pointer-events-auto animate-in fade-in slide-in-from-bottom-2 duration-200 border border-slate-700`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">{content}</div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/50 hover:text-white transition-colors p-0.5 flex-shrink-0"
              aria-label="Close tooltip"
              type="button"
            >
              <i className="ti ti-x text-sm" />
            </button>
          </div>
          <div className={arrowClasses[position]} />
        </div>
      )}
    </div>
  );
};

// ==========================================
// 3. STATISTICS CARDS COMPONENT
// ==========================================
const StatisticsCards = ({ stats, chargesTotal }) => {
  const cards = [
    {
      id: "revenue",
      title: "Total Revenue",
      value: formatCurrency(stats.totalRevenue),
      icon: "ti ti-currency-rupee",
      color: "blue",
      tooltip:
        "Total revenue generated from all vehicle bookings across the selected period.",
    },
    {
      id: "bookings",
      title: "Total Bookings",
      value: stats.bookingCount,
      icon: "ti ti-shopping-cart",
      color: "green",
      tooltip: "Total number of bookings completed across all vehicles.",
    },
    {
      id: "days",
      title: "Days Operated",
      value: stats.totalDaysOperated,
      icon: "ti ti-calendar",
      color: "yellow",
      tooltip:
        "Total number of days vehicles were operated during the selected period.",
    },
    {
      id: "vehicles",
      title: "Active Vehicles",
      value: `${stats.activeVehicles}/${stats.totalVehicles}`,
      subtitle: `${stats.totalVehicles - stats.activeVehicles} inactive`,
      icon: "ti ti-car",
      color: "purple",
      tooltip: "Active vehicles with revenue vs total vehicles in the fleet.",
    },
    {
      id: "targets",
      title: "Targets Met",
      value: `${stats.targetsMet}/${stats.targetsMet + stats.targetsNotMet}`,
      subtitle: `${stats.targetsNotMet} targets missed`,
      icon: "ti ti-trophy",
      color: "success",
      tooltip:
        "Number of vehicles that met their revenue targets vs those that did not.",
    },
    {
      id: "charges",
      title: "Delivery & Pickup Charges",
      value: formatCurrency(chargesTotal?.deliveryAndPickup || 0),
      icon: "ti ti-truck",
      color: "pink",
      tooltip:
        "Total delivery and pickup charges collected during the selected period.",
    },
  ];

  const colorClasses = {
    blue: "border-l-blue-500 bg-blue-50/30",
    green: "border-l-green-500 bg-green-50/30",
    yellow: "border-l-yellow-500 bg-yellow-50/30",
    purple: "border-l-purple-500 bg-purple-50/30",
    success: "border-l-emerald-500 bg-emerald-50/30",
    pink: "border-l-pink-500 bg-pink-50/30",
  };

  const iconBgClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    yellow: "bg-yellow-100 text-yellow-600",
    purple: "bg-purple-100 text-purple-600",
    success: "bg-emerald-100 text-emerald-600",
    pink: "bg-pink-100 text-pink-600",
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
      {cards.map((card) => (
        <div
          key={card.id}
          className={`bg-white rounded-xl border border-slate-200 border-l-4 ${colorClasses[card.color]} p-4 shadow-sm transition-all hover:shadow-md`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-gray-500 truncate">
                  {card.title}
                </span>
                <InfoTooltip content={card.tooltip} position="top" />
              </div>
              <div className="text-lg font-bold text-slate-800 mt-1 truncate">
                {card.value}
              </div>
              {card.subtitle && (
                <div className="text-[10px] text-gray-400 mt-0.5">
                  {card.subtitle}
                </div>
              )}
            </div>
            <div
              className={`p-2 rounded-lg flex-shrink-0 ${iconBgClasses[card.color]}`}
            >
              <i className={`${card.icon} text-sm`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==========================================
// 4. SALES REPORTS TAB CONTENT
// ==========================================
const SalesReportsTab = ({ stats, chargesTotal, byCity, byModel }) => {
  // Target achievement data for pie chart
  const targetData = [
    { name: "Target Met", value: stats.targetsMet },
    { name: "Target Not Met", value: stats.targetsNotMet },
  ];

  const COLORS = ["#22c55e", "#ef4444"];

  return (
    <div className="space-y-6">
      {/* Target Achievement & Revenue Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-medium text-slate-800 text-sm flex items-center gap-2 mb-4">
            <i className="ti ti-chart-pie text-amber-500" /> Target Achievement
            <InfoTooltip content="Distribution of vehicles that met their revenue targets vs those that did not." />
          </h3>
          <div className="h-[200px] flex items-center justify-center">
            <div className="relative w-full max-w-[200px]">
              <svg viewBox="0 0 100 100" className="w-full h-auto">
                {targetData.reduce((acc, item, index) => {
                  const total = targetData.reduce((sum, d) => sum + d.value, 0);
                  const percentage = total > 0 ? (item.value / total) * 100 : 0;
                  const startAngle = acc;
                  const angle = (percentage / 100) * 360;
                  const endAngle = startAngle + angle;

                  const x1 =
                    50 + 40 * Math.cos(((startAngle - 90) * Math.PI) / 180);
                  const y1 =
                    50 + 40 * Math.sin(((startAngle - 90) * Math.PI) / 180);
                  const x2 =
                    50 + 40 * Math.cos(((endAngle - 90) * Math.PI) / 180);
                  const y2 =
                    50 + 40 * Math.sin(((endAngle - 90) * Math.PI) / 180);

                  const largeArc = angle > 180 ? 1 : 0;

                  const path = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;

                  acc.push(
                    <path
                      key={index}
                      d={path}
                      fill={COLORS[index]}
                      stroke="white"
                      strokeWidth="1"
                    />,
                  );

                  return acc;
                }, [])}
                <circle cx="50" cy="50" r="25" fill="white" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-slate-800">
                  {stats.targetsMet + stats.targetsNotMet > 0
                    ? `${Math.round((stats.targetsMet / (stats.targetsMet + stats.targetsNotMet)) * 100)}%`
                    : "0%"}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2 ml-4">
              {targetData.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index] }}
                  ></span>
                  <span className="text-slate-600">{item.name}:</span>
                  <span className="font-medium text-slate-800">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-medium text-slate-800 text-sm flex items-center gap-2 mb-4">
            <i className="ti ti-building text-amber-500" /> Revenue by City
            <InfoTooltip content="Revenue distribution across different cities." />
          </h3>
          <div className="space-y-2">
            {byCity.slice(0, 5).map((city, index) => {
              const maxRevenue =
                byCity.length > 0
                  ? Math.max(...byCity.map((c) => c.totalRevenue))
                  : 1;
              const percentage =
                maxRevenue > 0 ? (city.totalRevenue / maxRevenue) * 100 : 0;
              const colors = [
                "#3b82f6",
                "#8b5cf6",
                "#ec4899",
                "#f59e0b",
                "#22c55e",
              ];

              return (
                <div key={city.city} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-medium text-slate-700">
                      {city.city}
                    </span>
                    <span className="text-gray-500">
                      {formatCurrency(city.totalRevenue)}
                    </span>
                  </div>
                  <div className="h-5 bg-gray-100 rounded-md overflow-hidden relative">
                    <div
                      className="h-full rounded-md transition-all duration-500 flex items-center px-2"
                      style={{
                        width: `${Math.max(percentage, 2)}%`,
                        backgroundColor: colors[index % colors.length],
                        opacity: 0.85,
                      }}
                    >
                      <span className="text-white text-[9px] font-bold">
                        {city.vehicles} vehicles
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {byCity.length > 5 && (
              <div className="text-xs text-gray-400 text-center pt-2">
                +{byCity.length - 5} more cities
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Revenue by Model Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h3 className="font-medium text-slate-800 text-sm flex items-center gap-2 mb-4">
          <i className="ti ti-chart-bar text-amber-500" /> Revenue by Vehicle
          Model
          <InfoTooltip content="Top vehicle models by revenue generated." />
        </h3>
        <div className="space-y-2">
          {byModel.slice(0, 10).map((model, index) => {
            const maxRevenue =
              byModel.length > 0
                ? Math.max(...byModel.map((m) => m.totalRevenue))
                : 1;
            const percentage =
              maxRevenue > 0 ? (model.totalRevenue / maxRevenue) * 100 : 0;
            const colors = [
              "#3b82f6",
              "#8b5cf6",
              "#ec4899",
              "#f59e0b",
              "#22c55e",
              "#06b6d4",
              "#f472b6",
              "#a78bfa",
              "#34d399",
              "#fb923c",
            ];

            return (
              <div key={model.model} className="space-y-0.5">
                <div className="flex justify-between text-xs">
                  <span className="font-medium text-slate-700 truncate max-w-[200px]">
                    {model.model}
                  </span>
                  <span className="text-gray-500">
                    {formatCurrency(model.totalRevenue)} ({model.bookingCount}{" "}
                    bookings)
                  </span>
                </div>
                <div className="h-4 bg-gray-100 rounded-md overflow-hidden relative">
                  <div
                    className="h-full rounded-md transition-all duration-500"
                    style={{
                      width: `${Math.max(percentage, 2)}%`,
                      backgroundColor: colors[index % colors.length],
                      opacity: 0.8,
                    }}
                  />
                </div>
              </div>
            );
          })}
          {/* {byModel.length > 10 && (
            <div className="text-xs text-gray-400 text-center pt-2">
              +{byModel.length - 10} more models
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 5. BY MODEL TAB CONTENT
// ==========================================
const ByModelTab = ({ byModel }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
        <span className="text-xs font-medium text-gray-500">
          {byModel.length} vehicle models
        </span>
        <InfoTooltip content="Performance metrics grouped by vehicle model. Shows revenue, bookings, and target achievement rates." />
      </div>

      {byModel.length === 0 ? (
        <div className="py-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-50 rounded-full mb-3 border border-slate-100">
            <i className="ti ti-car-off text-slate-400 text-xl" />
          </div>
          <h3 className="text-sm font-bold text-slate-700 mb-0.5">
            No models found
          </h3>
          <p className="text-xs text-gray-400">
            No vehicle data available for the selected period.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {byModel.map((model, index) => {
            const achievementRate =
              model.vehicles > 0
                ? (model.targetsMet / model.vehicles) * 100
                : 0;
            const isSuccess = achievementRate >= 50;

            return (
              <div
                key={model.model}
                className="px-5 py-4 hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-800 text-sm tracking-tight">
                        {model.model}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          isSuccess
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {achievementRate.toFixed(0)}% target rate
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                      <span>{model.vehicles} vehicles</span>
                      <span className="w-1 h-1 rounded-full bg-gray-300" />
                      <span>{model.bookingCount} bookings</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-right">
                      <div className="text-sm font-bold text-slate-800">
                        {formatCurrency(model.totalRevenue)}
                      </div>
                      <div className="text-[10px] text-gray-400">Revenue</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-700">
                        {model.targetsMet}/{model.vehicles}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        Targets Met
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==========================================
// 6. BY CITY TAB CONTENT
// ==========================================
const ByCityTab = ({ byCity }) => {
  const totalRevenue = byCity.reduce((sum, c) => sum + c.totalRevenue, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
        <span className="text-xs font-medium text-gray-500">
          {byCity.length} cities • Total Revenue: {formatCurrency(totalRevenue)}
        </span>
        <InfoTooltip content="Performance metrics grouped by city. Shows revenue, bookings, and target achievement rates per city." />
      </div>

      {byCity.length === 0 ? (
        <div className="py-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-50 rounded-full mb-3 border border-slate-100">
            <i className="ti ti-building-off text-slate-400 text-xl" />
          </div>
          <h3 className="text-sm font-bold text-slate-700 mb-0.5">
            No cities found
          </h3>
          <p className="text-xs text-gray-400">
            No city data available for the selected period.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {byCity.map((city) => {
            const achievementRate =
              city.vehicles > 0 ? (city.targetsMet / city.vehicles) * 100 : 0;
            const isSuccess = achievementRate >= 50;
            const revenueShare =
              totalRevenue > 0 ? (city.totalRevenue / totalRevenue) * 100 : 0;

            return (
              <div
                key={city.city}
                className="px-5 py-4 hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-slate-800 text-sm tracking-tight">
                        {city.city}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          isSuccess
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {achievementRate.toFixed(0)}% target rate
                      </span>
                      <span className="text-xs text-gray-400">
                        {revenueShare.toFixed(1)}% of total
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-bold text-slate-800">
                          {formatCurrency(city.totalRevenue)}
                        </div>
                        <div className="text-[10px] text-gray-400">Revenue</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-slate-700">
                          {city.bookingCount}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          Bookings
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <i className="ti ti-car text-gray-300" /> {city.vehicles}{" "}
                      vehicles
                    </span>
                    <span className="w-px h-3 bg-gray-200" />
                    <span className="flex items-center gap-1">
                      <i className="ti ti-trophy text-gray-300" />{" "}
                      {city.targetsMet} targets met
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==========================================
// 7. MONTHLY REVENUE TAB CONTENT
// ==========================================
const MonthlyRevenueTab = ({ monthlyList, inactiveVehicles }) => {
  const [visibleCount, setVisibleCount] = useState(15);
  const [showInactive, setShowInactive] = useState(false);

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="font-medium text-slate-700">
                Active Vehicles:
              </span>
              <span className="font-bold text-slate-900">
                {monthlyList.length}
              </span>
            </span>
            <span className="w-px h-4 bg-gray-200" />
            <span className="flex items-center gap-1.5">
              <span className="font-medium text-slate-700">Inactive:</span>
              <span className="font-bold text-slate-400">
                {inactiveVehicles.length}
              </span>
            </span>
            <span className="w-px h-4 bg-gray-200" />
            <span className="flex items-center gap-1.5">
              <span className="font-medium text-slate-700">Total Revenue:</span>
              <span className="font-bold text-emerald-600">
                {formatCurrency(
                  monthlyList.reduce((sum, v) => sum + v.totalRevenue, 0),
                )}
              </span>
            </span>
            <span className="w-px h-4 bg-gray-200" />
            <span className="flex items-center gap-1.5">
              <span className="font-medium text-slate-700">
                Total Bookings:
              </span>
              <span className="font-bold text-slate-900">
                {monthlyList.reduce((sum, v) => sum + v.bookingCount, 0)}
              </span>
            </span>
          </div>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className="text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
          >
            {showInactive ? "Hide" : "Show"} Inactive ({inactiveVehicles.length}
            )
          </button>
        </div>
      </div>

      {/* Active Vehicles Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
          <span className="text-xs font-medium text-gray-500">
            Showing {Math.min(visibleCount, monthlyList.length)} of{" "}
            {monthlyList.length} active vehicles
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Sorted by revenue ↓</span>
            <InfoTooltip content="Monthly revenue breakdown per vehicle. Shows individual performance metrics including revenue, bookings, days operated, and target achievement." />
          </div>
        </div>

        {monthlyList.length === 0 ? (
          <div className="py-12 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-50 rounded-full mb-3 border border-slate-100">
              <i className="ti ti-chart-bar-off text-slate-400 text-xl" />
            </div>
            <h3 className="text-sm font-bold text-slate-700 mb-0.5">
              No active vehicles
            </h3>
            <p className="text-xs text-gray-400">
              No vehicles with revenue in the selected period.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {monthlyList.slice(0, visibleCount).map((vehicle) => {
              const isTargetMet = vehicle.isTargetMet;

              return (
                <div
                  key={vehicle.id}
                  className="px-5 py-4 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {vehicle.imageUrl && (
                          <img
                            src={vehicle.imageUrl}
                            alt={vehicle.name}
                            className="w-8 h-8 rounded-lg object-cover border border-slate-200 flex-shrink-0"
                            onError={(e) => (e.target.style.display = "none")}
                          />
                        )}
                        <span className="font-bold text-slate-800 text-sm tracking-tight">
                          {vehicle.name || "N/A"}
                        </span>
                        <span className="text-xs text-gray-400 font-mono">
                          {vehicle.licensePlate || "N/A"}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            isTargetMet
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {isTargetMet ? "✓ Target Met" : "✗ Target Missed"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1 flex-wrap">
                        <span>{vehicle.vehicleModelText || "N/A"}</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300" />
                        <span>
                          {CITY_NORMALIZE_MAP[vehicle.cityText] ||
                            vehicle.cityText ||
                            "N/A"}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-gray-300" />
                        <span>{vehicle.bookingCount || 0} bookings</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300" />
                        <span>
                          {vehicle.totalDaysOperated || 0} days operated
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-right">
                        <div className="text-sm font-bold text-slate-800">
                          {formatCurrency(vehicle.totalRevenue)}
                        </div>
                        <div className="text-[10px] text-gray-400">Revenue</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-slate-700">
                          {formatCurrency(vehicle.averageRevenuePerDay || 0)}
                        </div>
                        <div className="text-[10px] text-gray-400">Avg/Day</div>
                      </div>
                      <div className="text-right">
                        <div
                          className={`text-sm font-bold ${isTargetMet ? "text-emerald-600" : "text-red-600"}`}
                        >
                          {vehicle.achivement?.toFixed(1) || "0"}%
                        </div>
                        <div className="text-[10px] text-gray-400">
                          Achievement
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {monthlyList.length > visibleCount && (
          <div className="p-3 bg-slate-50/50 text-center border-t border-slate-100">
            <button
              onClick={() => setVisibleCount((prev) => prev + 15)}
              className="px-4 py-1.5 bg-white hover:bg-slate-50 text-slate-700 font-semibold border border-slate-200 rounded-lg text-xs shadow-xs transition-colors"
            >
              Load more ({monthlyList.length - visibleCount} remaining)
            </button>
          </div>
        )}
      </div>

      {/* Inactive Vehicles */}
      {showInactive && inactiveVehicles.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
            <span className="text-xs font-medium text-gray-500">
              Inactive Vehicles ({inactiveVehicles.length})
            </span>
            <InfoTooltip content="Vehicles with zero revenue during the selected period." />
          </div>
          <div className="divide-y divide-slate-100">
            {inactiveVehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                className="px-5 py-3 hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {vehicle.imageUrl && (
                    <img
                      src={vehicle.imageUrl}
                      alt={vehicle.name}
                      className="w-8 h-8 rounded-lg object-cover border border-slate-200 flex-shrink-0"
                      onError={(e) => (e.target.style.display = "none")}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-700 text-sm">
                        {vehicle.name || "N/A"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {vehicle.licensePlate || "N/A"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {vehicle.vehicleModelText || "N/A"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      {CITY_NORMALIZE_MAP[vehicle.cityText] ||
                        vehicle.cityText ||
                        "N/A"}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    <span className="px-2 py-1 bg-slate-100 rounded-full">
                      No revenue
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 8. FILTERS BAR COMPONENT
// ==========================================
const FiltersBar = ({
  searchTerm,
  setSearchTerm,
  handleExportCSV,
  disableExport,
}) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center flex-1 min-w-[200px]">
          <div className="relative flex-1 min-w-[180px]">
            <label htmlFor="search-vehicles" className="sr-only">
              Search by vehicle name or license plate
            </label>
            <i className="ti ti-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
            <input
              id="search-vehicles"
              type="text"
              placeholder="Search by name or license..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-300"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            disabled={disableExport}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
              disableExport
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-amber-50 text-amber-700 hover:bg-amber-100"
            }`}
          >
            <i className="ti ti-download text-sm" />
            Export CSV
          </button>
          <InfoTooltip
            content="Export the current data as a CSV file."
            position="bottom"
          />
        </div>
      </div>

      {searchTerm.trim() !== "" && (
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100 mt-3">
          <span className="text-xs text-gray-400 mr-1">Active filters:</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
            Search: "{searchTerm}"
            <button
              onClick={() => setSearchTerm("")}
              className="ml-1 text-slate-400 hover:text-amber-600 focus:outline-none font-bold"
              aria-label="Remove search term"
            >
              ×
            </button>
          </span>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 9. MAIN DASHBOARD COMPONENT
// ==========================================
const VehiclegroupsalesDashboard = () => {
  const cities = useSelector((state) => state?.cities?.citiesData);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  const [selectionRange, setSelectionRange] = useState({
    startDate: oneMonthAgo,
    endDate: today,
    key: "selection",
  });
  const [endDate, setEndDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("sales-reports");
  const [rawData, setRawData] = useState([]);
  const [chargesTotal, setChargesTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [toast, setToast] = useState(null);
  const [city, setCity] = useState("");
  const [hub, setHub] = useState("");
  const [getAllHubsListData, setGetAllHubsListData] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Format date for API
  const formatDateForAPI = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}T00:00`;
  };

  const formatEndDateForAPI = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}T23:59`;
  };

  const getAllHubsList = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/hubs?limit=1000`);
      const data = await response.json();
      if (data?.statusCode === "0100")
        setGetAllHubsListData(data?.entities?.rows);
    } catch (error) {
      console.error("Error fetching City Names:", error);
    }
  };

  // Fetch data
  const fetchSalesData = async (cityFilter = city, hubFilter = hub) => {
    try {
      setLoading(true);
      setApiError(false);

      const start = formatDateForAPI(selectionRange?.startDate);
      const end = formatEndDateForAPI(selectionRange?.endDate);
      let query = "";
      if (cityFilter) {
        query += `&cityId=${cityFilter}`;
      }
      if (hubFilter) {
        query += `&hubId=${hubFilter}`;
      }

      const response = await fetch(
        `${apiUrl}/api/dashboardreports/vehicle-wise-revenue-details?startDate=${start}&endDate=${end}${query}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );

      const data = await response.json();

      if (data?.statusCode === "0100") {
        setRawData(data?.dataValues?.dataList || []);
        setChargesTotal(data?.dataValues?.chargesTotal || null);
      } else {
        setRawData([]);
        setChargesTotal(null);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching sales data:", error);
      setRawData([]);
      setChargesTotal(null);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, [selectionRange]);

  useEffect(() => {
    getAllHubsList();
  }, []);

  // --- Data Derivation using useMemo ---

  // 1. Summary Statistics
  const summaryStats = useMemo(() => {
    if (!rawData.length)
      return {
        totalRevenue: 0,
        bookingCount: 0,
        totalDaysOperated: 0,
        totalVehicles: 0,
        activeVehicles: 0,
        targetsMet: 0,
        targetsNotMet: 0,
        averageRevenue: 0,
      };

    const totalVehicles = rawData.length;
    const activeVehicles = rawData.filter((v) => v.totalRevenue > 0).length;
    const targetsMet = rawData.filter((v) => v.isTargetMet).length;
    const targetsNotMet = rawData.filter((v) => !v.isTargetMet).length;

    const totals = rawData.reduce(
      (acc, vehicle) => ({
        totalRevenue: acc.totalRevenue + (vehicle.totalRevenue || 0),
        bookingCount: acc.bookingCount + (vehicle.bookingCount || 0),
        totalDaysOperated:
          acc.totalDaysOperated + (vehicle.totalDaysOperated || 0),
      }),
      { totalRevenue: 0, bookingCount: 0, totalDaysOperated: 0 },
    );

    return {
      ...totals,
      totalVehicles,
      activeVehicles,
      targetsMet,
      targetsNotMet,
      averageRevenue:
        activeVehicles > 0 ? totals.totalRevenue / activeVehicles : 0,
    };
  }, [rawData]);

  // 2. Group by Vehicle Model
  const byModel = useMemo(() => {
    if (!rawData.length) return [];

    const modelMap = rawData.reduce((acc, vehicle) => {
      const model = vehicle.vehicleModelText || "Unknown";
      if (!acc[model]) {
        acc[model] = {
          model,
          totalRevenue: 0,
          bookingCount: 0,
          vehicles: 0,
          targetsMet: 0,
        };
      }
      acc[model].totalRevenue += vehicle.totalRevenue || 0;
      acc[model].bookingCount += vehicle.bookingCount || 0;
      acc[model].vehicles += 1;
      if (vehicle.isTargetMet) acc[model].targetsMet += 1;
      return acc;
    }, {});

    return Object.values(modelMap).sort(
      (a, b) => b.totalRevenue - a.totalRevenue,
    );
  }, [rawData]);

  // 3. Group by City (with normalization)
  const byCity = useMemo(() => {
    if (!rawData.length) return [];

    const cityMap = rawData.reduce((acc, vehicle) => {
      const rawCity = vehicle.cityText || "Unknown";
      const city = CITY_NORMALIZE_MAP[rawCity] || rawCity;

      if (!acc[city]) {
        acc[city] = {
          city,
          totalRevenue: 0,
          bookingCount: 0,
          vehicles: 0,
          targetsMet: 0,
        };
      }
      acc[city].totalRevenue += vehicle.totalRevenue || 0;
      acc[city].bookingCount += vehicle.bookingCount || 0;
      acc[city].vehicles += 1;
      if (vehicle.isTargetMet) acc[city].targetsMet += 1;
      return acc;
    }, {});

    return Object.values(cityMap).sort(
      (a, b) => b.totalRevenue - a.totalRevenue,
    );
  }, [rawData]);

  // 4. Monthly Revenue List (filtered and sorted)
  const monthlyList = useMemo(() => {
    if (!rawData.length) return [];

    let filtered = rawData
      .filter((vehicle) => vehicle.totalRevenue > 0)
      .sort((a, b) => b.totalRevenue - a.totalRevenue);

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          (v.name || "").toLowerCase().includes(term) ||
          (v.licensePlate || "").toLowerCase().includes(term) ||
          (v.vehicleModelText || "").toLowerCase().includes(term),
      );
    }

    return filtered;
  }, [rawData, searchTerm]);

  const inactiveVehicles = useMemo(() => {
    if (!rawData.length) return [];

    let filtered = rawData.filter((vehicle) => vehicle.totalRevenue === 0);

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          (v.name || "").toLowerCase().includes(term) ||
          (v.licensePlate || "").toLowerCase().includes(term) ||
          (v.vehicleModelText || "").toLowerCase().includes(term),
      );
    }

    return filtered;
  }, [rawData, searchTerm]);

  // Export CSV
  const handleExportCSV = () => {
    const escapeCSV = (val) => {
      if (val === undefined || val === null) return '""';
      const cleanStr = String(val).replace(/"/g, '""');
      return cleanStr.includes(",") ||
        cleanStr.includes('"') ||
        cleanStr.includes("\n")
        ? `"${cleanStr}"`
        : cleanStr;
    };

    const exportData = monthlyList.length > 0 ? monthlyList : rawData;

    const headers = [
      "Vehicle ID",
      "Name",
      "License Plate",
      "City",
      "Model",
      "Total Revenue",
      "Bookings",
      "Days Operated",
      "Avg/Day",
      "Achievement %",
      "Target Met",
    ];

    const rows = exportData.map((v) => [
      v.id || "",
      v.name || "",
      v.licensePlate || "",
      v.cityText || "",
      v.vehicleModelText || "",
      v.totalRevenue || 0,
      v.bookingCount || 0,
      v.totalDaysOperated || 0,
      v.averageRevenuePerDay || 0,
      v.achivement || 0,
      v.isTargetMet ? "Yes" : "No",
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map(escapeCSV).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `Sales_Report_${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast("CSV exported successfully!", "success");
  };

  // Skeleton loading state
  const SkeletonState = () => (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-24 bg-slate-200 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="h-52 bg-slate-200 rounded-xl" />
        <div className="h-52 bg-slate-200 rounded-xl" />
      </div>
      <div className="h-80 bg-slate-200 rounded-xl" />
    </div>
  );

  // Tab configuration
  const tabs = [
    { id: "sales-reports", label: "Sales Reports", icon: "ti ti-chart-pie" },
    { id: "by-model", label: "By Model", icon: "ti ti-car" },
    { id: "by-city", label: "By City", icon: "ti ti-building" },
    { id: "monthly-revenue", label: "Monthly Revenue", icon: "ti ti-calendar" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans text-slate-800 my-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            Vehicle Group Sales Dashboard
            <InfoTooltip content="Comprehensive dashboard for monitoring vehicle sales performance, revenue, and target achievement across different models and cities." />
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitor vehicle revenue performance and target achievement.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DaterangePicker
            selectionRange={selectionRange}
            setSelectionRange={setSelectionRange}
            style={-10}
          />
          <select
            className="w-40 h-11 px-4 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 shadow-sm outline-none appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          >
            <option value="">Select City</option>
            {cities &&
              Object.entries(cities).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
          </select>
          <select
            className="w-40 h-11 px-4 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 shadow-sm outline-none appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
            value={hub}
            onChange={(e) => setHub(e.target.value)}
          >
            <option value="">Select Hub</option>
            {getAllHubsListData &&
              getAllHubsListData.map((item, index) => (
                <option key={index} value={item?.id}>
                  {item?.hubName}
                </option>
              ))}
          </select>

          <button
            onClick={() => {
              fetchSalesData();
            }}
            className="p-2 bg-blue-400"
          >
            Search
          </button>
          <button
            onClick={() => {
              setCity("");
              setHub("");
              const resetRange = {
                startDate: oneMonthAgo,
                endDate: today,
                key: "selection",
              };
              setSelectionRange(resetRange);
              fetchSalesData("", "");
            }}
            className="p-2 bg-red-400"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Error state */}
      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-6 flex items-center justify-between shadow-xs">
          <p className="text-red-700 text-xs font-medium flex items-center gap-2">
            <i className="ti ti-alert-triangle text-sm" /> Failed to fetch sales
            data. Please check your connection and try again.
          </p>
          <button
            onClick={fetchSalesData}
            className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-bold transition-all"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <SkeletonState />
      ) : (
        <>
          {/* Statistics Cards */}
          <StatisticsCards stats={summaryStats} chargesTotal={chargesTotal} />

          {/* Filters Bar */}
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleExportCSV={handleExportCSV}
            disableExport={rawData.length === 0}
          />

          {/* Tabs */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="border-b border-slate-200">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      px-5 py-3 text-sm font-medium whitespace-nowrap transition-all
                      flex items-center gap-2 border-b-2
                      ${
                        activeTab === tab.id
                          ? "border-amber-500 text-amber-600 bg-amber-50/50"
                          : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                      }
                    `}
                  >
                    <i className={`${tab.icon} text-sm`} />
                    {tab.label}
                    {activeTab === tab.id && (
                      <span className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-500" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-5">
              {activeTab === "sales-reports" && (
                <SalesReportsTab
                  stats={summaryStats}
                  chargesTotal={chargesTotal}
                  byCity={byCity}
                  byModel={byModel}
                />
              )}
              {activeTab === "by-model" && <ByModelTab byModel={byModel} />}
              {activeTab === "by-city" && <ByCityTab byCity={byCity} />}
              {activeTab === "monthly-revenue" && (
                <MonthlyRevenueTab
                  monthlyList={monthlyList}
                  inactiveVehicles={inactiveVehicles}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className="rounded-xl bg-slate-900 text-white font-medium shadow-xl px-4 py-3 flex items-center gap-2.5 text-xs border border-slate-800">
            <i className="ti ti-circle-check text-emerald-400 text-sm" />
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclegroupsalesDashboard;
